import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { MdCheck, MdVisibility } from 'react-icons/md';
import type { RootState } from '../../../redux/store';
import { setNailheadFile, setNailheadSize } from '../../../redux/slices/customizationSlice';
import type { NailheadSize } from '../../../types/customization.types';
import type { StudConfig, StudImage } from '../customizerConfig';
import SwatchPreviewModal from '../shared/SwatchPreviewModal';
import { THEME, SPRING } from '../theme';

const DEFAULT_STUD_IMAGES: StudImage[] = [
  { path: '/assets/ralph_input_images/stud.png', label: 'Brass Nailhead' },
];

interface NailheadSectionProps {
  /** Product-level stud configuration. Defaults to original behaviour if omitted. */
  studConfig?: StudConfig;
}

const NailheadSection: React.FC<NailheadSectionProps> = ({ studConfig }) => {
  const dispatch = useDispatch();
  const { nailheadFile, nailheadSize } = useSelector((s: RootState) => s.customization);
  const apiSizes = useSelector((s: RootState) => s.customization.swatches.nailheadSizes);

  const showSize = studConfig?.showSize ?? true;
  const studImages = studConfig?.images ?? DEFAULT_STUD_IMAGES;

  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [hovered, setHovered] = useState<number | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLabel, setPreviewLabel] = useState('');

  // Auto-set default size
  useEffect(() => {
    if (!nailheadSize) {
      if (showSize && apiSizes.length > 0) {
        const defaultSize = apiSizes.includes('M') ? 'M' : apiSizes[0];
        dispatch(setNailheadSize(defaultSize as NailheadSize));
      } else {
        dispatch(setNailheadSize('M'));
      }
    }
  }, [apiSizes, nailheadSize, dispatch, showSize]);

  // Force 'M' when size picker is hidden
  useEffect(() => {
    if (!showSize && nailheadSize !== 'M') {
      dispatch(setNailheadSize('M'));
    }
  }, [showSize, nailheadSize]);

  // Auto-load selected stud file into Redux
  useEffect(() => {
    const img = studImages[selectedImageIdx];
    if (!img) return;
    (async () => {
      try {
        const res = await fetch(img.path);
        const blob = await res.blob();
        const filename = img.path.split('/').pop() || 'stud.png';
        const file = new File([blob], filename, { type: blob.type || 'image/png' });
        dispatch(setNailheadFile(file));
      } catch {
        console.warn('[NailheadSection] Could not load stud image');
      }
    })();
  }, [selectedImageIdx, studImages]);

  const sizeOptions = apiSizes.map((s) => ({ value: s, label: s }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25 }}
      className="space-y-5"
    >
      {/* ── Row: Stud image(s) + Size ──────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[11px] font-bold uppercase tracking-wider text-white/40">Stud</span>
          <div className="flex-1 h-px" style={{ background: THEME.border }} />
        </div>

        <div className="flex items-start gap-4">
          {/* Stud image swatches */}
          <div className="flex gap-3 flex-shrink-0 flex-wrap">
            {studImages.map((img, idx) => {
              const isSelected = selectedImageIdx === idx;
              const isHovered = hovered === idx;
              return (
                <motion.div
                  key={idx}
                  className="flex flex-col items-center gap-1.5 flex-shrink-0"
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  onHoverStart={() => setHovered(idx)}
                  onHoverEnd={() => setHovered(null)}
                >
                  <button
                    onClick={() => setSelectedImageIdx(idx)}
                    className="relative cursor-pointer group"
                  >
                    <div
                      className="rounded-2xl overflow-hidden transition-all duration-300"
                      style={{
                        width: 88,
                        height: 88,
                        boxShadow: isSelected ? `0 0 0 2.5px ${THEME.primary}, ${THEME.primaryGlow}` : 'none',
                        border: isSelected ? 'none' : '1.5px solid rgba(255,255,255,0.08)',
                        background: 'rgba(20,20,24,0.9)',
                      }}
                    >
                      <img
                        src={img.path}
                        alt={img.label}
                        className={`w-full h-full object-contain p-2 transition-all duration-300 ${
                          isSelected ? 'brightness-100 scale-105' : 'brightness-65 group-hover:brightness-90'
                        }`}
                        loading="lazy"
                      />
                    </div>
                    {/* Eye icon */}
                    <AnimatePresence>
                      {isHovered && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.5 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.5 }}
                          transition={SPRING.snappy}
                          className="absolute -top-1.5 -right-1.5 z-20"
                        >
                          <motion.div
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewUrl(img.path);
                              setPreviewLabel(img.label);
                              setPreviewOpen(true);
                            }}
                            className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer"
                            style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', border: '1.5px solid rgba(255,255,255,0.25)', boxShadow: '0 2px 10px rgba(0,0,0,0.4)' }}
                          >
                            <MdVisibility size={13} className="text-white/90" />
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    {/* Check */}
                    <AnimatePresence>
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                          transition={SPRING.snappy}
                          className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ background: THEME.primary, boxShadow: THEME.primaryGlow }}
                        >
                          <MdCheck size={12} className="text-white" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </button>
                  {studImages.length > 1 && (
                    <span className="text-[9px] text-white/30 text-center max-w-[88px] truncate">{img.label}</span>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Right side: Size in same row — conditionally shown */}
          {showSize && (
            <div className="flex-1 flex items-start gap-4 min-w-0 pt-1">
              <div className="flex-shrink-0">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/35 block mb-1.5">Size</span>
                {sizeOptions.length > 0 ? (
                  <div className="flex gap-1.5">
                    {sizeOptions.map((opt) => {
                      const isActive = nailheadSize === opt.value;
                      return (
                        <motion.button
                          key={opt.value}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => dispatch(setNailheadSize(opt.value as NailheadSize))}
                          className="w-8 h-8 rounded-lg text-[11px] font-semibold cursor-pointer transition-all duration-200 flex items-center justify-center"
                          style={{
                            background: isActive ? THEME.primary : 'rgba(255,255,255,0.04)',
                            color: isActive ? '#fff' : 'rgba(255,255,255,0.45)',
                            border: `1px solid ${isActive ? THEME.primary : 'rgba(255,255,255,0.08)'}`,
                            boxShadow: isActive ? THEME.primaryGlow : 'none',
                          }}
                        >
                          {opt.label}
                        </motion.button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex gap-1.5">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="w-8 h-8 rounded-lg bg-white/5 animate-pulse" />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <SwatchPreviewModal isOpen={previewOpen} imageUrl={previewUrl} label={previewLabel} onClose={() => setPreviewOpen(false)} />
    </motion.div>
  );
};

export default NailheadSection;
